import { create } from 'zustand';

import { findFormat } from '../config/platforms';
import { AdBackground, AdElement, AdScene, createBackground } from './types';

const HISTORY_LIMIT = 50;
/** Zmiany z tym samym kluczem szybciej niż co tyle milisekund trafiają do jednego wpisu historii. */
const HISTORY_MERGE_MS = 800;

export type LayerMove = 'up' | 'down' | 'front' | 'back';

/** Rodzaj zapisanego wpisu: gotowa kreacja albo szablon do ponownego użycia. */
export type SavedKind = 'creative' | 'template';

/** Powiązanie planszy z wpisem w bibliotece — puste, dopóki kreacja nie ma zapisu. */
export interface SavedRef {
  id: string;
  name: string;
  kind: SavedKind;
}

interface AdsGeneratorState {
  scene: AdScene | null;
  /**
   * Zaznaczenie jako LISTA, nie pojedyncze id: wyrównanie i rozkład elementów
   * mają sens dopiero na kilku naraz, a inspektor i tak pokazuje właściwości
   * tylko wtedy, gdy zaznaczony jest dokładnie jeden.
   */
  selectedIds: string[];
  zoom: number;
  past: AdScene[];
  future: AdScene[];
  /** Wpis w bibliotece, który edytujemy. `null` = plansza jeszcze niezapisana. */
  saved: SavedRef | null;
  /** Klucz i czas ostatniego wpisu historii, do sklejania serii drobnych zmian. */
  historyKey: string | null;
  historyAt: number;

  startScene: (platformId: string, formatId: string) => void;
  closeScene: () => void;
  select: (id: string | null) => void;
  /** Dokłada albo zdejmuje element z zaznaczenia (Shift + klik). */
  toggleSelect: (id: string) => void;
  selectMany: (ids: string[]) => void;
  setZoom: (zoom: number) => void;

  /**
   * Zapisuje stan sprzed zmiany. Wołane raz na gest (np. na starcie
   * przeciągania), a kolejne aktualizacje idą już z `silent`.
   */
  pushHistory: () => void;
  /**
   * Zapis historii dla zmian ciągłych (suwak, próbnik koloru). Kolejne wywołania
   * z tym samym kluczem w krótkim odstępie nie tworzą nowego wpisu, więc jedno
   * przeciągnięcie suwaka cofa się jednym Ctrl+Z.
   */
  commit: (key?: string) => void;
  undo: () => void;
  redo: () => void;

  addElement: (element: AdElement) => void;
  /** Wstawia komplet elementów (gotowy element kreacji) jako JEDEN krok historii. */
  addElements: (elements: AdElement[]) => void;
  updateElement: (id: string, patch: Partial<AdElement>, silent?: boolean) => void;
  removeElement: (id: string) => void;
  duplicateElement: (id: string) => void;
  moveLayer: (id: string, move: LayerMove) => void;
  setBackground: (patch: Partial<AdBackground>, silent?: boolean) => void;
  /** Wstawia gotową scenę (otwarcie zapisanej kreacji). Historia startuje od zera. */
  loadScene: (scene: AdScene, saved: SavedRef) => void;
  markSaved: (saved: SavedRef) => void;
  replaceElements: (elements: AdElement[], background?: AdBackground) => void;
}

const withHistory = (state: AdsGeneratorState): Pick<AdsGeneratorState, 'past' | 'future'> => ({
  past: state.scene ? [...state.past, state.scene].slice(-HISTORY_LIMIT) : state.past,
  future: [],
});

const reorder = (elements: AdElement[], index: number, move: LayerMove): AdElement[] => {
  const next = [...elements];
  const [element] = next.splice(index, 1);
  const targetIndex = {
    up: Math.min(index + 1, next.length),
    down: Math.max(index - 1, 0),
    front: next.length,
    back: 0,
  }[move];
  next.splice(targetIndex, 0, element);
  return next;
};

export const useAdsGeneratorStore = create<AdsGeneratorState>((set) => ({
  scene: null,
  selectedIds: [],
  zoom: 1,
  past: [],
  future: [],
  saved: null,
  historyKey: null,
  historyAt: 0,

  startScene: (platformId, formatId) => {
    const format = findFormat(platformId, formatId);
    if (!format) return;
    set({
      saved: null,
      scene: {
        platformId,
        formatId,
        width: format.width,
        height: format.height,
        background: createBackground(),
        elements: [],
      },
      selectedIds: [],
      past: [],
      future: [],
      zoom: 1,
    });
  },

  closeScene: () => set({ scene: null, selectedIds: [], past: [], future: [], saved: null }),

  select: (id) => set({ selectedIds: id ? [id] : [] }),

  toggleSelect: (id) => set((state) => ({
    selectedIds: state.selectedIds.includes(id)
      ? state.selectedIds.filter((item) => item !== id)
      : [...state.selectedIds, id],
  })),

  selectMany: (ids) => set({ selectedIds: ids }),

  setZoom: (zoom) => set({ zoom: Math.min(Math.max(zoom, 0.05), 3) }),

  pushHistory: () => set((state) => ({ ...withHistory(state), historyKey: null, historyAt: Date.now() })),

  commit: (key) => set((state) => {
    if (!state.scene) return state;
    const now = Date.now();
    if (key && state.historyKey === key && now - state.historyAt < HISTORY_MERGE_MS) {
      return { historyAt: now };
    }
    return { ...withHistory(state), historyKey: key ?? null, historyAt: now };
  }),

  undo: () => set((state) => {
    const previous = state.past[state.past.length - 1];
    if (!previous || !state.scene) return state;
    return {
      scene: previous,
      past: state.past.slice(0, -1),
      future: [state.scene, ...state.future].slice(0, HISTORY_LIMIT),
      selectedIds: state.selectedIds.filter((id) => previous.elements.some((element) => element.id === id)),
    };
  }),

  redo: () => set((state) => {
    const [next, ...rest] = state.future;
    if (!next || !state.scene) return state;
    return {
      scene: next,
      past: [...state.past, state.scene].slice(-HISTORY_LIMIT),
      future: rest,
      selectedIds: state.selectedIds.filter((id) => next.elements.some((element) => element.id === id)),
    };
  }),

  addElement: (element) => set((state) => {
    if (!state.scene) return state;
    return {
      ...withHistory(state),
      scene: { ...state.scene, elements: [...state.scene.elements, element] },
      selectedIds: [element.id],
    };
  }),

  addElements: (elements) => set((state) => {
    if (!state.scene || elements.length === 0) return state;
    return {
      ...withHistory(state),
      scene: { ...state.scene, elements: [...state.scene.elements, ...elements] },
      selectedIds: elements.map((element) => element.id),
    };
  }),

  updateElement: (id, patch, silent) => set((state) => {
    if (!state.scene) return state;
    const elements = state.scene.elements.map((element) => (
      element.id === id ? { ...element, ...patch } as AdElement : element
    ));
    return {
      ...(silent ? {} : withHistory(state)),
      scene: { ...state.scene, elements },
    };
  }),

  removeElement: (id) => set((state) => {
    if (!state.scene) return state;
    return {
      ...withHistory(state),
      scene: { ...state.scene, elements: state.scene.elements.filter((element) => element.id !== id) },
      selectedIds: state.selectedIds.filter((selected) => selected !== id),
    };
  }),

  duplicateElement: (id) => set((state) => {
    if (!state.scene) return state;
    const index = state.scene.elements.findIndex((element) => element.id === id);
    if (index === -1) return state;
    const source = state.scene.elements[index];
    const offset = Math.round(Math.min(state.scene.width, state.scene.height) * 0.03);
    const copy = {
      ...source,
      id: `${source.id}-copy-${Date.now()}`,
      name: `${source.name} (kopia)`,
      x: source.x + offset,
      y: source.y + offset,
    } as AdElement;
    const elements = [...state.scene.elements];
    elements.splice(index + 1, 0, copy);
    return {
      ...withHistory(state),
      scene: { ...state.scene, elements },
      selectedIds: [copy.id],
    };
  }),

  moveLayer: (id, move) => set((state) => {
    if (!state.scene) return state;
    const index = state.scene.elements.findIndex((element) => element.id === id);
    if (index === -1) return state;
    return {
      ...withHistory(state),
      scene: { ...state.scene, elements: reorder(state.scene.elements, index, move) },
    };
  }),

  setBackground: (patch, silent) => set((state) => {
    if (!state.scene) return state;
    return {
      ...(silent ? {} : withHistory(state)),
      scene: { ...state.scene, background: { ...state.scene.background, ...patch } },
    };
  }),

  loadScene: (scene, saved) => set({
    scene,
    saved,
    selectedIds: [],
    past: [],
    future: [],
    zoom: 1,
  }),

  markSaved: (saved) => set({ saved }),

  replaceElements: (elements, background) => set((state) => {
    if (!state.scene) return state;
    return {
      ...withHistory(state),
      scene: {
        ...state.scene,
        elements,
        background: background ?? state.scene.background,
      },
      selectedIds: [],
    };
  }),
}));

/**
 * Element do inspektora: tylko przy zaznaczeniu dokładnie jednego. Przy kilku
 * pokazujemy akcje zbiorcze, bo edycja kroju „wszystkich naraz" prowadziłaby do
 * nadpisywania ustawień, których użytkownik nie widzi.
 */
export const useSelectedElement = (): AdElement | null => useAdsGeneratorStore((state) => {
  if (!state.scene || state.selectedIds.length !== 1) return null;
  return state.scene.elements.find((element) => element.id === state.selectedIds[0]) ?? null;
});

/** Wszystkie zaznaczone elementy, w kolejności rysowania. */
export const useSelectedElements = (): AdElement[] => useAdsGeneratorStore((state) => {
  if (!state.scene) return [];
  return state.scene.elements.filter((element) => state.selectedIds.includes(element.id));
});

export const { getState: getAdsGeneratorState } = useAdsGeneratorStore;
